import { CheckCircle2, CircleOff, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableActionsMenu } from "@/components/shared/TableActionsMenu";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { UsePaginationReturn } from "@/hooks/usePagination";
import type { CertificateProductPriceRow } from "@/services/platformCertificatePricingService";
import {
  formatCertificatePricingChannelBadges,
  formatCertificatePricingProduct,
} from "@/utils/certificatePricingDisplay";

function formatMoneyUy(minor: number): string {
  return (minor / 100).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function urAndMarginLabels(r: CertificateProductPriceRow): {
  urLabel: string;
  marginLabel: string;
} {
  const urDisplay = Number.parseFloat(r.urUnits);
  const urLabel = Number.isFinite(urDisplay)
    ? urDisplay.toLocaleString("es-UY", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      })
    : r.urUnits;
  const marginDisplay = Number.parseFloat(r.marginPercent);
  const marginLabel = Number.isFinite(marginDisplay)
    ? `${marginDisplay}%`
    : r.marginPercent;
  return { urLabel, marginLabel };
}

export type CertificatePricingProductsTableProps = {
  /** Rows after status filter (used for list and pagination). */
  rows: CertificateProductPriceRow[];
  /** Total products before filter; used to distinguish “no data” vs “no matches”. */
  totalUnfilteredCount: number;
  paginatedRows: CertificateProductPriceRow[];
  pagination: UsePaginationReturn;
  columnCount: number;
  /** Row whose active state is being saved (disables that row’s actions). */
  togglingProductId: string | null;
  onEdit: (row: CertificateProductPriceRow) => void;
  onToggleActive: (row: CertificateProductPriceRow) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
};

export function CertificatePricingProductsTable({
  rows,
  totalUnfilteredCount,
  paginatedRows,
  pagination,
  columnCount,
  togglingProductId,
  onEdit,
  onToggleActive,
  onRefresh,
  isRefreshing,
}: CertificatePricingProductsTableProps): JSX.Element {
  const { t } = useTranslation();

  const showCatalogEmpty = totalUnfilteredCount === 0;
  const showFilterEmpty = totalUnfilteredCount > 0 && rows.length === 0;

  return (
    <TableCard className="mt-4">
      {showCatalogEmpty ? (
        <ResponsiveListSplit
          table={
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("platform.products.colChannels")}</TableHead>
                    <TableHead>{t("platform.products.colType")}</TableHead>
                    <TableHead>{t("platform.products.colFixed")}</TableHead>
                    <TableHead>{t("platform.products.colUrUnits")}</TableHead>
                    <TableHead>{t("platform.products.colMargin")}</TableHead>
                    <TableHead>{t("platform.products.colPerFileStorage")}</TableHead>
                    <TableHead>{t("platform.products.colDeliveryFee")}</TableHead>
                    <TableHead>{t("platform.products.colStamp")}</TableHead>
                    <TableHead className="w-[100px]">{t("platform.products.colActive")}</TableHead>
                    <TableHead className="w-[80px]">{t("platform.products.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={columnCount} className="h-32">
                      <EmptyState
                        title={t("platform.products.emptyTitle")}
                        message={t("platform.products.emptyMessage")}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          }
          cards={
            <div className="flex min-h-[32vh] items-center justify-center p-4">
              <EmptyState
                title={t("platform.products.emptyTitle")}
                message={t("platform.products.emptyMessage")}
              />
            </div>
          }
        />
      ) : showFilterEmpty ? (
        <ResponsiveListSplit
          table={
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("platform.products.colChannels")}</TableHead>
                    <TableHead>{t("platform.products.colType")}</TableHead>
                    <TableHead>{t("platform.products.colFixed")}</TableHead>
                    <TableHead>{t("platform.products.colUrUnits")}</TableHead>
                    <TableHead>{t("platform.products.colMargin")}</TableHead>
                    <TableHead>{t("platform.products.colPerFileStorage")}</TableHead>
                    <TableHead>{t("platform.products.colDeliveryFee")}</TableHead>
                    <TableHead>{t("platform.products.colStamp")}</TableHead>
                    <TableHead className="w-[100px]">{t("platform.products.colActive")}</TableHead>
                    <TableHead className="w-[80px]">{t("platform.products.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={columnCount} className="h-32">
                      <EmptyState
                        title={t("platform.products.emptyFilterTitle")}
                        message={t("platform.products.emptyFilterMessage")}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          }
          cards={
            <div className="flex min-h-[32vh] items-center justify-center p-4">
              <EmptyState
                title={t("platform.products.emptyFilterTitle")}
                message={t("platform.products.emptyFilterMessage")}
              />
            </div>
          }
        />
      ) : (
        <ResponsiveListSplit
          table={
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("platform.products.colChannels")}</TableHead>
                    <TableHead>{t("platform.products.colType")}</TableHead>
                    <TableHead>{t("platform.products.colFixed")}</TableHead>
                    <TableHead>{t("platform.products.colUrUnits")}</TableHead>
                    <TableHead>{t("platform.products.colMargin")}</TableHead>
                    <TableHead>{t("platform.products.colPerFileStorage")}</TableHead>
                    <TableHead>{t("platform.products.colDeliveryFee")}</TableHead>
                    <TableHead>{t("platform.products.colStamp")}</TableHead>
                    <TableHead className="w-[100px]">{t("platform.products.colActive")}</TableHead>
                    <TableHead className="w-[80px]">{t("platform.products.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.map((r) => {
                    const { urLabel, marginLabel } = urAndMarginLabels(r);
                    const rowBusy = togglingProductId === r.id;
                    return (
                      <TableRow key={r.id}>
                        <TableCell>
                          <div className="flex max-w-[9rem] flex-wrap gap-1">
                            {formatCertificatePricingChannelBadges(
                              r.involvesAccountant,
                              r.involvesNotary,
                              t,
                            ).map((label) => (
                              <Badge key={label} variant="secondary" className="text-xs font-normal">
                                {label}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatCertificatePricingProduct(r.targetType, t)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums text-foreground">
                          $ {formatMoneyUy(r.fixedAmountMinor)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </TableCell>
                        <TableCell className="tabular-nums text-foreground">{urLabel}</TableCell>
                        <TableCell className="tabular-nums text-foreground">{marginLabel}</TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums text-foreground">
                          $ {formatMoneyUy(r.perFileStorageMinor ?? 0)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums text-foreground">
                          $ {formatMoneyUy(r.deliveryFeeMinor ?? 0)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums text-foreground">
                          $ {formatMoneyUy(r.professionalStampMinor ?? 0)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge variant={r.isActive ? "success" : "info"}>
                            {r.isActive
                              ? t("platform.products.activeYes")
                              : t("platform.products.activeNo")}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          <TableActionsMenu
                            disabled={rowBusy}
                            actions={[
                              {
                                label: t("platform.products.edit"),
                                icon: Pencil,
                                onClick: () => onEdit(r),
                              },
                              r.isActive
                                ? {
                                    label: t("platform.products.rowActionDeactivate"),
                                    icon: CircleOff,
                                    onClick: () => onToggleActive(r),
                                  }
                                : {
                                    label: t("platform.products.rowActionActivate"),
                                    icon: CheckCircle2,
                                    onClick: () => onToggleActive(r),
                                  },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          }
          cards={
            <div className="divide-y divide-border">
              {paginatedRows.map((r) => {
                const { urLabel, marginLabel } = urAndMarginLabels(r);
                const rowBusy = togglingProductId === r.id;
                return (
                  <DataListCardRow key={r.id}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 space-y-2">
                        <p className="font-semibold leading-snug text-foreground">
                          {formatCertificatePricingProduct(r.targetType, t)}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {formatCertificatePricingChannelBadges(
                            r.involvesAccountant,
                            r.involvesNotary,
                            t,
                          ).map((label) => (
                            <Badge key={label} variant="secondary" className="text-xs font-normal">
                              {label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="shrink-0">
                        <TableActionsMenu
                          disabled={rowBusy}
                          actions={[
                            {
                              label: t("platform.products.edit"),
                              icon: Pencil,
                              onClick: () => onEdit(r),
                            },
                            r.isActive
                              ? {
                                  label: t("platform.products.rowActionDeactivate"),
                                  icon: CircleOff,
                                  onClick: () => onToggleActive(r),
                                }
                              : {
                                  label: t("platform.products.rowActionActivate"),
                                  icon: CheckCircle2,
                                  onClick: () => onToggleActive(r),
                                },
                          ]}
                        />
                      </div>
                    </div>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <DataListCardField label={t("platform.products.colFixed")}>
                        <span className="tabular-nums text-foreground">
                          $ {formatMoneyUy(r.fixedAmountMinor)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </span>
                      </DataListCardField>
                      <DataListCardField label={t("platform.products.colUrUnits")}>
                        <span className="tabular-nums">{urLabel}</span>
                      </DataListCardField>
                      <DataListCardField label={t("platform.products.colMargin")}>
                        <span className="tabular-nums">{marginLabel}</span>
                      </DataListCardField>
                      <DataListCardField label={t("platform.products.colPerFileStorage")}>
                        <span className="tabular-nums text-foreground">
                          $ {formatMoneyUy(r.perFileStorageMinor ?? 0)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </span>
                      </DataListCardField>
                      <DataListCardField label={t("platform.products.colDeliveryFee")}>
                        <span className="tabular-nums text-foreground">
                          $ {formatMoneyUy(r.deliveryFeeMinor ?? 0)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </span>
                      </DataListCardField>
                      <DataListCardField label={t("platform.products.colStamp")}>
                        <span className="tabular-nums text-foreground">
                          $ {formatMoneyUy(r.professionalStampMinor ?? 0)}{" "}
                          <span className="text-xs text-muted-foreground">UYU</span>
                        </span>
                      </DataListCardField>
                      <DataListCardField label={t("platform.products.colActive")}>
                        <StatusBadge variant={r.isActive ? "success" : "info"}>
                          {r.isActive
                            ? t("platform.products.activeYes")
                            : t("platform.products.activeNo")}
                        </StatusBadge>
                      </DataListCardField>
                    </div>
                  </DataListCardRow>
                );
              })}
            </div>
          }
        />
      )}
      {!showCatalogEmpty && !showFilterEmpty ? (
        <TablePagination
          pagination={pagination}
          rowsPerPageLabel={t("platform.products.rowsPerPage")}
          pageOfLabel={t("platform.products.pageOf", {
            from: (pagination.page - 1) * pagination.limit + 1,
            to: Math.min(pagination.page * pagination.limit, pagination.total),
            total: pagination.total,
          })}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      ) : null}
    </TableCard>
  );
}
